import { customElement, property, html, type TemplateResult, css, cssManager } from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-quantityselector.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-quantityselector': DeesInputQuantitySelector;
  }
}

@customElement('dees-input-quantityselector')
export class DeesInputQuantitySelector extends DeesInputBase<DeesInputQuantitySelector> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property({
    type: Number
  })
  accessor value: number = 1;



  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
        /* TODO: Migrate hardcoded values to --dees-* CSS variables */
        :host {
          width: auto;
          user-select: none;
        }

        .quantity-container {
          font-size: var(--dees-font-control-size);
          display: inline-flex;
          align-items: center;
          height: var(--dees-input-height);
          padding: 0;
          min-width: 120px;
          color: var(--dees-color-text-primary);
          border-radius: var(--dees-radius-lg);
          overflow: hidden;
        }
        
        .quantity-container.disabled {
          background: var(--dees-color-badge-default-bg);
          border-color: var(--dees-color-border-default);
          opacity: 0.5;
          pointer-events: none;
        }

        .quantity-container:hover:not(.disabled) {
          border-color: var(--dees-color-border-strong);
        }

        .quantity-container:focus-within {
          box-shadow: none;
        }

        .selector {
          border: 0;
          background: transparent;
          font: inherit;
          flex: 0 0 40px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          color: var(--dees-color-text-secondary);
          position: relative;
        }

        .selector:hover {
          background: var(--dees-color-badge-default-bg);
          color: var(--dees-color-text-primary);
        }

        .selector:active {
          background: var(--dees-color-border-subtle);
        }

        .selector.minus {
          border-right: 1px solid var(--dees-color-border-default);
        }

        .selector.plus {
          border-left: 1px solid var(--dees-color-border-default);
        }

        .quantity {
          flex: 1;
          text-align: center;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.006em;
        }
        :host([label-position="left"]) .quantity-container { justify-self: end; width: min(160px, 100%); }

        /* Keyboard navigation focus styles */
        .selector:focus {
          outline: none;
          background: var(--dees-color-badge-default-bg);
          z-index: 1;
        }

        /* Min value state */
        .quantity-container[data-min="true"] .selector.minus {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .quantity-container[data-min="true"] .selector.minus:hover {
          background: transparent;
          color: var(--dees-color-text-muted);
        }

    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        ${this.label ? html`<dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>` : ''}
        <div 
          class="quantity-container field-control ${this.disabled ? 'disabled' : ''}"
          data-min="${this.value <= 0}"
        >
          <button type="button"
            class="selector minus input-action"
            ?disabled=${this.disabled || this.value <= 0}
            @click="${() => {this.decrease();}}"
            aria-label="Decrease quantity"
          ><dees-icon icon="lucide:Minus" iconSize="14"></dees-icon></button>
          <div class="quantity" aria-live="polite" aria-atomic="true">${this.value}</div>
          <button type="button"
            class="selector plus input-action"
            ?disabled=${this.disabled}
            @click="${() => {this.increase();}}"
            aria-label="Increase quantity"
          ><dees-icon icon="lucide:Plus" iconSize="14"></dees-icon></button>
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  public increase() {
    if (!this.disabled) {
      this.value++;
      this.changeSubject.next(this);
      this.dispatchEvent(new CustomEvent('newValue', { detail: this.value, bubbles: true, composed: true }));
    }
  }

  public decrease() {
    if (!this.disabled && this.value > 0) {
      this.value--;
      this.changeSubject.next(this);
      this.dispatchEvent(new CustomEvent('newValue', { detail: this.value, bubbles: true, composed: true }));
    }
  }

  public getValue(): number {
    return this.value;
  }

  public setValue(value: number): void {
    this.value = value;
  }
}
